/*
 * Pure derivations for the live run view.
 *
 * An in-flight run usually has little or no `execution` data — the verifier
 * streams `RunEvent`s long before step results are durable. Everything here
 * turns that event stream into renderable structure without touching the
 * shared logic in src/logic.ts:
 *
 * - `derivePhaseSpine` answers "is the agent still working the test out, or
 *   already replaying it?" and where the run is in its lifecycle.
 * - `deriveLiveAttempts` synthesizes attempts and steps from events when the
 *   run's own `attempts` array is absent or behind the stream.
 * - `applyEventsToRun` projects a display copy of the run from the events
 *   revealed so far (used by the fixture-mode simulation).
 */
import type {
  AttemptPurpose,
  RunAttempt,
  RunEvent,
  RunState,
  RunStepResult,
  WebTestRun
} from '@blume/web-test-model'

export type PhaseId = Exclude<RunState, 'terminal' | 'cancelling'>
export type PhaseStatus = 'done' | 'current' | 'upcoming' | 'not-reached'

export interface SpinePhase {
  id: PhaseId
  label: string
  hint: string
  status: PhaseStatus
}

export interface PhaseSpine {
  /** Authoring: the agent is working the test out. Replay: the plan is trusted. */
  kind: 'authoring' | 'replay'
  phases: SpinePhase[]
  cancelling: boolean
  terminal: boolean
  /** The at-a-glance answer, one sentence. */
  sentence: string
}

export const phaseLabel: Record<PhaseId, string> = {
  queued: 'Queued',
  preparing: 'Preparing',
  exploring: 'Exploring',
  compiling: 'Compiling',
  stabilizing: 'Stabilizing',
  running: 'Running',
  diagnosing: 'Diagnosing'
}

export const phaseHint: Record<PhaseId, string> = {
  queued: 'Waiting for a verifier worker',
  preparing: 'Provisioning the browser and the target app',
  exploring: 'The agent drives a real browser and narrates what it tries',
  compiling: 'The successful trace becomes a declarative plan with stable locators',
  stabilizing: 'Clean deterministic replays prove the plan is trustworthy',
  running: 'Deterministic replay of the committed plan',
  diagnosing: 'Working out whose fault the failure is'
}

/** Phases that mean the agent is still authoring the test. */
export const authoringPhases: ReadonlySet<PhaseId> = new Set([
  'exploring',
  'compiling',
  'stabilizing'
])

const authoringCanonical: PhaseId[] = [
  'queued',
  'preparing',
  'exploring',
  'compiling',
  'stabilizing'
]
const replayCanonical: PhaseId[] = ['queued', 'preparing', 'running']

const authoringAttemptPurposes: ReadonlySet<AttemptPurpose> = new Set([
  'discovery',
  'stabilization',
  'repair-discovery',
  'repair-validation'
])

function isPhaseId(state: RunState): state is PhaseId {
  return state !== 'terminal' && state !== 'cancelling'
}

function eventsForRun(run: WebTestRun, events: RunEvent[]): RunEvent[] {
  return [...events]
    .filter((event) => event.runId === run.id)
    .sort((left, right) => left.sequence - right.sequence)
}

/** States observed in the stream, in first-seen order. */
function observedPhases(run: WebTestRun, runEvents: RunEvent[]): PhaseId[] {
  const seen: PhaseId[] = []
  const seenSet = new Set<PhaseId>()
  for (const event of runEvents) {
    if (event.state !== undefined && isPhaseId(event.state) && !seenSet.has(event.state)) {
      seen.push(event.state)
      seenSet.add(event.state)
    }
  }
  if (isPhaseId(run.state) && !seenSet.has(run.state)) seen.push(run.state)
  return seen
}

export function derivePhaseSpine(run: WebTestRun, events: RunEvent[]): PhaseSpine {
  const runEvents = eventsForRun(run, events)
  const observed = observedPhases(run, runEvents)
  const observedSet = new Set(observed)

  const attemptsSuggestAuthoring = run.attempts.some((attempt) =>
    authoringAttemptPurposes.has(attempt.purpose)
  )
  const kind: PhaseSpine['kind'] =
    [...observedSet].some((phase) => authoringPhases.has(phase)) ||
    attemptsSuggestAuthoring ||
    (run.planHash === undefined && !observedSet.has('running'))
      ? 'authoring'
      : 'replay'

  const canonical = kind === 'authoring' ? authoringCanonical : replayCanonical
  const merged: PhaseId[] = [...observed]
  const mergedSet = new Set(merged)
  for (const phase of canonical) {
    if (!mergedSet.has(phase)) {
      merged.push(phase)
      mergedSet.add(phase)
    }
  }

  const terminal = run.state === 'terminal'
  const cancelling = run.state === 'cancelling'
  const current = isPhaseId(run.state) ? run.state : undefined

  const phases: SpinePhase[] = merged.map((id) => {
    const status: PhaseStatus =
      id === current
        ? 'current'
        : observedSet.has(id)
          ? 'done'
          : terminal || cancelling
            ? 'not-reached'
            : 'upcoming'
    return { id, label: phaseLabel[id], hint: phaseHint[id], status }
  })

  return {
    kind,
    phases,
    cancelling,
    terminal,
    sentence: spineSentence(run, kind, cancelling, terminal)
  }
}

function spineSentence(
  run: WebTestRun,
  kind: PhaseSpine['kind'],
  cancelling: boolean,
  terminal: boolean
): string {
  if (cancelling) return 'Cancel requested — the run is stopping safely.'
  if (terminal) return 'This run has finished.'
  if (run.state === 'diagnosing') return 'Something failed — deciding whose fault it is.'
  if (run.state === 'queued' || run.state === 'preparing') {
    return kind === 'authoring'
      ? 'Getting ready — the agent will work out this test in a real browser.'
      : 'Getting ready to replay the committed plan.'
  }
  if (kind === 'authoring') {
    if (run.state === 'exploring') {
      return 'The agent is still working out this test — driving a real browser and narrating.'
    }
    if (run.state === 'compiling') {
      return 'The agent is still working out this test — compiling the trace into a stable plan.'
    }
    return 'The agent is still working out this test — proving the plan with clean replays.'
  }
  return 'Replaying the committed plan deterministically — the test itself is already trusted.'
}

/* ------------------------------------------------------------------ */
/* Attempt synthesis from events                                       */
/* ------------------------------------------------------------------ */

export interface DerivedAttempts {
  attempts: RunAttempt[]
  /**
   * Events whose content was folded into a synthetic step (the step-started
   * summary becomes the step description); the stream must skip these to
   * avoid narrating the same line twice.
   */
  consumedEventIds: ReadonlySet<string>
}

function inferAttemptPurpose(summary: string, run: WebTestRun): AttemptPurpose {
  const text = summary.toLowerCase()
  if (text.includes('repair') && text.includes('valid')) return 'repair-validation'
  if (text.includes('repair')) return 'repair-discovery'
  if (text.includes('stabiliz') || text.includes('clean replay')) return 'stabilization'
  if (text.includes('discover') || text.includes('explor')) return 'discovery'
  if (text.includes('retry')) return 'clean-retry'
  if (run.state === 'exploring') return 'discovery'
  if (run.state === 'stabilizing') return 'stabilization'
  return 'deterministic'
}

/**
 * Return the run's attempts extended with structure derived from the event
 * stream: attempts announced by `attempt-started` events that have no durable
 * record yet, and steps announced by `step-started` events that are missing
 * from an attempt's `steps`. Terminal runs with full execution pass through
 * unchanged.
 */
export function deriveLiveAttempts(run: WebTestRun, events: RunEvent[]): DerivedAttempts {
  const runEvents = eventsForRun(run, events)
  const consumed = new Set<string>()

  // Deep-enough copies so synthesis never mutates fixture objects.
  const attempts: RunAttempt[] = run.attempts.map((attempt) => ({
    ...attempt,
    steps: attempt.steps.map((step) => ({ ...step, artifactIds: [...step.artifactIds] })),
    artifactIds: [...attempt.artifactIds]
  }))
  const attemptById = new Map(attempts.map((attempt) => [attempt.id, attempt]))
  const stepByAttemptId = new Map(
    attempts.map((attempt) => [attempt.id, new Map(attempt.steps.map((step) => [step.id, step]))])
  )
  const attemptArtifactIds = new Map(
    attempts.map((attempt) => [attempt.id, new Set(attempt.artifactIds)])
  )
  const stepArtifactIds = new Map(
    attempts.flatMap((attempt) =>
      attempt.steps.map(
        (step) => [`${attempt.id}\u0000${step.id}`, new Set(step.artifactIds)] as const
      )
    )
  )

  for (const event of runEvents) {
    if (event.type === 'attempt-started' && event.attemptId !== undefined) {
      if (!attemptById.has(event.attemptId)) {
        const synthetic: RunAttempt = {
          id: event.attemptId,
          number: attempts.length + 1,
          purpose: inferAttemptPurpose(event.summary, run),
          startedAt: event.occurredAt,
          steps: [],
          artifactIds: []
        }
        attempts.push(synthetic)
        attemptById.set(synthetic.id, synthetic)
        stepByAttemptId.set(synthetic.id, new Map())
        attemptArtifactIds.set(synthetic.id, new Set())
      }
      continue
    }

    const attempt = event.attemptId ? attemptById.get(event.attemptId) : undefined
    if (!attempt) continue

    if (event.type === 'step-started' && event.stepId !== undefined) {
      const stepsById = stepByAttemptId.get(attempt.id)!
      if (!stepsById.has(event.stepId)) {
        const step: RunStepResult = {
          id: event.stepId,
          scenarioStepId: event.stepId,
          description: event.summary,
          status: 'running',
          startedAt: event.occurredAt,
          artifactIds: []
        }
        attempt.steps.push(step)
        stepsById.set(step.id, step)
        stepArtifactIds.set(`${attempt.id}\u0000${step.id}`, new Set())
        consumed.add(event.id)
      }
      continue
    }

    if (event.type === 'step-finished' && event.stepId !== undefined) {
      const step = stepByAttemptId.get(attempt.id)?.get(event.stepId)
      if (step && step.status === 'running') {
        step.status = event.status === 'failed' ? 'failed' : 'passed'
        step.finishedAt = event.occurredAt
        const started = step.startedAt ? new Date(step.startedAt).getTime() : undefined
        const finished = new Date(event.occurredAt).getTime()
        if (started !== undefined && Number.isFinite(started) && Number.isFinite(finished)) {
          step.durationMs = Math.max(0, finished - started)
        }
      }
      continue
    }

    if (event.type === 'attempt-finished') {
      if (attempt.status === undefined && event.status !== undefined) {
        attempt.status = event.status
      }
      if (attempt.finishedAt === undefined) attempt.finishedAt = event.occurredAt
      continue
    }

    if (event.type === 'artifact-created' && event.artifactId !== undefined) {
      if (event.stepId !== undefined) {
        const step = stepByAttemptId.get(attempt.id)?.get(event.stepId)
        const artifactIds = stepArtifactIds.get(`${attempt.id}\u0000${event.stepId}`)
        if (step && artifactIds && !artifactIds.has(event.artifactId)) {
          step.artifactIds.push(event.artifactId)
          artifactIds.add(event.artifactId)
        }
      } else if (!attemptArtifactIds.get(attempt.id)?.has(event.artifactId)) {
        attempt.artifactIds.push(event.artifactId)
        attemptArtifactIds.get(attempt.id)?.add(event.artifactId)
      }
    }
  }

  return { attempts, consumedEventIds: consumed }
}

/* ------------------------------------------------------------------ */
/* Event projection (fixture-mode simulation)                          */
/* ------------------------------------------------------------------ */

/**
 * Project a display copy of the run from the events revealed so far. Used by
 * the fixture simulation: early in the replay the run header must say
 * "Queued", not the fixture's final state. Never mutates the input run.
 */
export function applyEventsToRun(run: WebTestRun, revealed: RunEvent[]): WebTestRun {
  const runEvents = eventsForRun(run, revealed)
  if (runEvents.length === 0) return run
  let state: RunState | undefined
  let status = undefined as WebTestRun['status']
  let classification = undefined as WebTestRun['classification']
  let summary: string | undefined
  for (const event of runEvents) {
    if (event.state !== undefined) state = event.state
    if (event.status !== undefined) status = event.status
    if (event.classification !== undefined) classification = event.classification
    summary = event.summary
  }
  const nextState = state ?? run.state
  const terminal = nextState === 'terminal'
  return {
    ...run,
    state: nextState,
    ...(terminal ? { status: status ?? run.status ?? 'needs-review' } : { status: undefined }),
    ...(classification !== undefined ? { classification } : {}),
    ...(summary !== undefined ? { currentStageSummary: summary } : {})
  }
}
